import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { purchasesAPI, productsAPI, suppliersAPI, warehousesAPI } from '../../services/api';
import BarcodeScanner from '../common/BarcodeScanner';
import toast from 'react-hot-toast';

export default function GRNForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [supplierInvoiceNo, setSupplierInvoiceNo] = useState('');
  const [supplierInvoiceDate, setSupplierInvoiceDate] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  useEffect(() => {
    suppliersAPI.list({ limit: 200 }).then(r => setSuppliers(r.data.suppliers)).catch(() => {});
    warehousesAPI.list().then(r => { setWarehouses(r.data); const def = r.data.find(w => w.is_default); if (def) setWarehouseId(def.id); }).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (productSearch.length < 2) return setProductResults([]);
      try { const r = await productsAPI.list({ search: productSearch, limit: 10 }); setProductResults(r.data.products); setShowSearch(true); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [productSearch]);

  const addProduct = (p) => {
    setShowSearch(false); setProductSearch('');
    setItems(prev => [...prev, {
      product_id: p.id, product_name: p.name, sku: p.sku,
      batch_number: '', manufacturing_date: '', expiry_date: '',
      quantity_received: 1, quantity_accepted: 1, quantity_rejected: 0, free_quantity: 0,
      unit_price: Number(p.purchase_price) || 0, mrp: Number(p.mrp) || 0,
      selling_price: Number(p.selling_price) || 0, discount_pct: 0, gst_rate: Number(p.gst_rate) || 12
    }]);
  };

  const updateItem = (idx, field, value) => setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const handleBarcodeScan = async (barcode) => {
    setShowScanner(false);
    try {
      const res = await productsAPI.lookupBarcode(barcode);
      if (res.data) { toast.success(`Found: ${res.data.name}`); addProduct(res.data); return; }
    } catch {}
    try {
      const r = await productsAPI.list({ search: barcode, limit: 1 });
      if (r.data.products.length > 0) { toast.success(`Found: ${r.data.products[0].name}`); addProduct(r.data.products[0]); }
      else toast.error(`No product for barcode: ${barcode}`);
    } catch { toast.error(`Not found: ${barcode}`); }
  };

  const handleSubmit = async () => {
    if (!supplierId || !warehouseId) return toast.error('Select supplier and warehouse');
    if (items.length === 0) return toast.error('Add items');
    for (const item of items) { if (!item.batch_number || !item.expiry_date) return toast.error(`Batch & expiry required for ${item.product_name}`); }
    setLoading(true);
    try {
      const res = await purchasesAPI.createGRN({ supplier_id: supplierId, warehouse_id: warehouseId, supplier_invoice_no: supplierInvoiceNo, supplier_invoice_date: supplierInvoiceDate || null, notes, items });
      toast.success(`GRN created: ${res.data.grn_number}`);
      navigate('/purchases/grn');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div className="page-header"><h1>New Goods Received Note</h1><button onClick={() => navigate('/purchases/grn')} className="btn btn-secondary">Cancel</button></div>
      <div className="card mb-4"><div className="card-header"><h2>Receipt Details</h2></div><div className="card-body">
        <div className="form-row form-row-4">
          <div className="form-group"><label className="form-label">Supplier *</label><select className="form-select" value={supplierId} onChange={e => setSupplierId(e.target.value)}><option value="">Select</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
          <div className="form-group"><label className="form-label">Warehouse *</label><select className="form-select" value={warehouseId} onChange={e => setWarehouseId(e.target.value)}><option value="">Select</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
          <div className="form-group"><label className="form-label">Supplier Invoice #</label><input className="form-input" value={supplierInvoiceNo} onChange={e => setSupplierInvoiceNo(e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Supplier Invoice Date</label><input type="date" className="form-input" value={supplierInvoiceDate} onChange={e => setSupplierInvoiceDate(e.target.value)} /></div>
        </div>
      </div></div>

      <div className="card mb-4"><div className="card-header"><h2>Received Items</h2></div><div className="card-body">
        <div style={{ display:'flex', gap:10, marginBottom:16 }}>
          <div style={{ position:'relative', flex:1 }}>
            <input className="form-input" placeholder="Search products..." value={productSearch} onChange={e => setProductSearch(e.target.value)} />
          {showSearch && productResults.length > 0 && <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1px solid var(--border)', borderRadius:8, boxShadow:'var(--shadow-md)', zIndex:20, maxHeight:250, overflowY:'auto' }}>
            {productResults.map(p => <div key={p.id} onClick={() => addProduct(p)} style={{padding:'10px 14px',cursor:'pointer',borderBottom:'1px solid var(--border-light)'}} onMouseEnter={e=>e.currentTarget.style.background='#f8f9fa'} onMouseLeave={e=>e.currentTarget.style.background='#fff'}><strong>{p.name}</strong><div className="text-xs text-secondary">{p.sku}</div></div>)}
          </div>}
          </div>
          <button onClick={() => setShowScanner(true)} className="btn btn-accent" style={{ whiteSpace:'nowrap', height:40 }}>📷 Scan</button>
        </div>
        {showScanner && <BarcodeScanner onScan={handleBarcodeScan} onClose={() => setShowScanner(false)} title="Scan Product Barcode" continuous={true} />}
        {items.length > 0 ? <div className="table-container" style={{overflowX:'auto'}}><table style={{minWidth:1200}}>
          <thead><tr><th>#</th><th>Product</th><th>Batch # *</th><th>Mfg Date</th><th>Expiry *</th><th>Rcvd</th><th>Accepted</th><th>Free</th><th>Rate</th><th>MRP</th><th>Sell Price</th><th>GST%</th><th></th></tr></thead>
          <tbody>{items.map((item, idx) => (
            <tr key={idx}>
              <td>{idx+1}</td><td style={{fontWeight:600}}>{item.product_name}<br/><span className="text-xs text-secondary">{item.sku}</span></td>
              <td><input className="form-input" style={{width:100,padding:'4px 6px'}} value={item.batch_number} onChange={e => updateItem(idx,'batch_number',e.target.value)} placeholder="Batch" /></td>
              <td><input type="date" className="form-input" style={{width:130,padding:'4px 6px'}} value={item.manufacturing_date} onChange={e => updateItem(idx,'manufacturing_date',e.target.value)} /></td>
              <td><input type="date" className="form-input" style={{width:130,padding:'4px 6px'}} value={item.expiry_date} onChange={e => updateItem(idx,'expiry_date',e.target.value)} /></td>
              <td><input type="number" min="1" className="form-input" style={{width:60,padding:'4px 6px'}} value={item.quantity_received} onChange={e => { const v = Number(e.target.value)||1; updateItem(idx,'quantity_received',v); updateItem(idx,'quantity_accepted',v); }} /></td>
              <td><input type="number" min="0" className="form-input" style={{width:60,padding:'4px 6px'}} value={item.quantity_accepted} onChange={e => updateItem(idx,'quantity_accepted',Number(e.target.value)||0)} /></td>
              <td><input type="number" min="0" className="form-input" style={{width:55,padding:'4px 6px'}} value={item.free_quantity} onChange={e => updateItem(idx,'free_quantity',Number(e.target.value)||0)} /></td>
              <td><input type="number" step="0.01" className="form-input" style={{width:80,padding:'4px 6px'}} value={item.unit_price} onChange={e => updateItem(idx,'unit_price',Number(e.target.value)||0)} /></td>
              <td><input type="number" step="0.01" className="form-input" style={{width:80,padding:'4px 6px'}} value={item.mrp} onChange={e => updateItem(idx,'mrp',Number(e.target.value)||0)} /></td>
              <td><input type="number" step="0.01" className="form-input" style={{width:80,padding:'4px 6px'}} value={item.selling_price} onChange={e => updateItem(idx,'selling_price',Number(e.target.value)||0)} /></td>
              <td className="td-mono">{item.gst_rate}%</td>
              <td><button onClick={() => removeItem(idx)} style={{color:'var(--danger)',background:'transparent',border:'none',cursor:'pointer',fontSize:16}}>✕</button></td>
            </tr>
          ))}</tbody>
        </table></div> : <div className="empty-state" style={{padding:32}}><h3>No items</h3><p>Search products to add received goods</p></div>}
      </div></div>
      {items.length > 0 && <div className="flex gap-3" style={{paddingBottom:40}}>
        <button onClick={handleSubmit} className="btn btn-primary btn-lg" disabled={loading}>{loading ? 'Creating...' : 'Create GRN & Update Stock'}</button>
      </div>}
    </div>
  );
}
